import React, { useMemo } from 'react';
import { DateTime } from 'luxon';
import { ISnowfallItem } from '@/api/resort/types';
import { SnowUnit } from '@/components/blocks/Unit';

// Vertical bar chart of monthly snowfall — each bar is a column whose
// height encodes (snowfall / max) and is positioned at the bottom of
// the 70-px-tall data row.

const wrapperClass = [
  'mb-5',
  'max-sm:mt-5',
  'md:min-w-[220px] md:pt-2.5',
].join(' ');

const dataClass = [
  'flex items-end justify-between',
  'w-full h-[70px] pb-5 pr-9',
  'max-sm:pr-0',
  'md:p-0',
].join(' ');

const barClass = [
  'relative w-[18px] bg-dark-blue rounded-[99px]',
  'flex flex-col justify-between',
  'max-sm:mx-0.5',
].join(' ');

const valueClass = [
  'relative -top-[22px] text-center font-medium text-dark-blue',
  'text-sm leading-[1.275rem]',
  'max-md:text-xs max-md:leading-[1.115625rem]',
  'max-sm:absolute max-sm:w-full max-sm:text-[0.53125rem] max-sm:leading-[0.85rem]',
  'md:text-[.7rem]',
].join(' ');

const dateClass = [
  'absolute -bottom-[25px] w-full text-center font-medium uppercase text-dark-blue',
  'text-sm leading-[1.275rem]',
  'max-md:text-xs max-md:leading-[1.115625rem] max-md:-bottom-6',
  'max-sm:text-[0.53125rem] max-sm:leading-[0.85rem]',
  'md:text-[.7rem]',
].join(' ');

export const BestTimeSnow = ({
  snowfallInfo,
}: {
  snowfallInfo: Array<ISnowfallItem>,
}) => {
  const lines = (() => {
    const maxValueLine = Math.max(...snowfallInfo.map((item) => item.totalSnow));
    const minValueLine = Math.min(...snowfallInfo.map((item) => item.totalSnow));

    return [
      Math.ceil(maxValueLine + (maxValueLine * 0.2)),
      Math.ceil(maxValueLine - (maxValueLine / 4)),
      Math.ceil(maxValueLine - (maxValueLine / 4) * 2),
      Math.ceil(maxValueLine - (maxValueLine / 4) * 3),
      minValueLine - (maxValueLine * 0.2) >= 0 ? Math.ceil(minValueLine - (maxValueLine * 0.2)) : minValueLine,
    ];
  })();
  const showMonth = (d: string) => {
    const date = DateTime.fromJSDate(new Date(d));

    return date.monthShort;
  };

  const filteredMonths = useMemo<Array<ISnowfallItem>>(
    () => {
      let isOneMonthWithoutSnow = false;
      return snowfallInfo.filter((item) => {
        if (item.totalSnow === 0) {
          if (!isOneMonthWithoutSnow) {
            isOneMonthWithoutSnow = true;
            return true;
          }
          return false;
        }
        return true;
      });
    },
    [snowfallInfo],
  );

  return (
    <div className={wrapperClass}>
      <div className="relative px-3">
        <div className={dataClass}>
          {filteredMonths.map((snowfall) => (
            <div
              key={snowfall.date}
              className={barClass}
              style={{ height: `${((snowfall.totalSnow * 100) / lines[0]).toFixed(0)}%` }}
            >
              <div className={valueClass}>
                <SnowUnit values={snowfall.totalSnow} />
              </div>
              <div className={dateClass}>{showMonth(snowfall.date)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
